/**
 * Follow-ups — the Admin "Today" page's rules (Admin redesign Phase A0, 2026-10-06).
 *
 * Plan: C:\Users\nilay\.claude\plans\eager-painting-hejlsberg.md — "Today 3a" and "Phase A — A0".
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * ── WHAT IT IS ─────────────────────────────────────────────────────────────────────────────────
 * The redesigned /app/admin (design "Today 3a") replaces AdminPage.vue's six AdminTable widgets
 * with ONE merged worklist: chips (All · Tasks · Recall · Unscheduled · Broken · Claims · Balances ·
 * Pre-auths), the sections "Your tasks" / "Due today" / "Past due", a scope toggle (Everyone /
 * My queues), a sort, search-as-you-type, and a "Done today" rail with the combined call goals.
 * Every number on that page comes from the functions below, fed by the SAME
 * runQuery({ type: 'ADMIN_DASHBOARD' }) result the old page uses (`adminData`, seven arrays).
 *
 * The OLD page (AdminPage.vue's widgets, AdminTable.vue) stays untouched and keeps serving everyone
 * whose uiVersion.js STAGE.admin / office choice says classic. Nothing here is read by the old page.
 *
 * ── THE AGREEMENT WITH THE OLD PAGE ────────────────────────────────────────────────────────────
 * isTodayRow is the old `*DataToday` filter, character for character in meaning:
 *     moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.<Queue>StatusUpdatedAt).isSame(moment(), 'day')
 *       || t.dueToday === 'true' || t.pastDue === 'true'
 * cypress/node-tests/followups-dashboard-rules.test.js runs the filter text it reads OUT OF
 * AdminPage.vue (with moment) next to this file on one fixture and fails on any difference, and
 * pins the CASL subjects and adminData indexes to the widgets'. Two moment details are kept:
 *   • moment(undefined) is NOW, so a row with NO CreatedAt key always passes; null / '' never do.
 *   • the "day" is moment's default zone — main.js sets America/New_York, NOT the site's zone.
 *     Pass `today` = the same moment().format('YYYY-MM-DD') the page sends as the query's `d`.
 * And "completed" is the old widgets' conversion: anything but the string 'false' (or boolean
 * false) is completed — so a row with no `completed` field counts as done, as it did there.
 *
 * Open = passes isTodayRow, not completed, dueToday or pastDue. Done = passes and completed.
 * Everything else that passes is DROPPED (the old widgets listed those rows unchecked — see
 * splitDashboardRows). adminData[4] (processed pre-auths) has no widget and no queue here.
 *
 * Times stay raw (the DB's 'YYYY-MM-DD HH:mm:ss' strings, dateStrings: true); formatting and the
 * time zone of what is SHOWN are the caller's.
 */

/** The old page's moment default zone (main.js `moment.tz.setDefault("America/New_York")`). */
export const DEFAULT_TIME_ZONE = 'America/New_York';

/**
 * The six queues, in the page's display order. Field by field:
 *   key                 queues.js QUEUES[].key (source-guarded by the node-test)
 *   index               the adminData array (ADMIN_DASHBOARD result set, main-service sql/admin-dashboard.js)
 *   subject             the CASL subject the old widget checks ($can('view', X) || $can('view', 'all'))
 *   statusUpdatedField  the row's "worked at" column — the old `*DataToday` filter's second date
 *   amountField         the column "Largest amount first" sorts by; null = the queue has none.
 *                       ⚠ ADMIN_DASHBOARD rows carry only PatientAmount (balances) today; ClaimFee /
 *                       officeFeeTotal / Fee are the names the queue pages use (rowModel.js) and read
 *                       as "no amount" (sorted last) until the query selects them.
 *   goalSetting         the daily call goal setting, or null
 *   responsibleSetting  queues.js responsibleEmployeeSetting ("id" or "id|id|…")
 *   label               the chip label
 */
export const DASHBOARD_QUEUES = [
  {
    key: 'overdueRecall',
    index: 0,
    subject: 'overdue_recall',
    statusUpdatedField: 'OverdueRecallStatusUpdatedAt',
    amountField: null,
    goalSetting: 'OVERDUE_RECALL_DAILY_CALLS_GOAL',
    responsibleSetting: 'OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID',
    label: 'Recall',
  },
  {
    key: 'unscheduledTreatment',
    index: 1,
    subject: 'unscheduled_treatment',
    statusUpdatedField: 'UnscheduledTreatmentStatusUpdatedAt',
    amountField: 'officeFeeTotal',
    goalSetting: 'UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL',
    responsibleSetting: 'UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID',
    label: 'Unscheduled',
  },
  {
    key: 'brokenAppointments',
    index: 2,
    subject: 'broken_appointment',
    statusUpdatedField: 'BrokenAppointmentStatusUpdatedAt',
    amountField: 'Fee',
    goalSetting: null,
    responsibleSetting: 'BROKEN_APPOINTMENTS_RESPONSIBLE_EMPLOYEEID',
    label: 'Broken',
  },
  {
    key: 'outstandingClaims',
    index: 3,
    subject: 'outstanding_claim',
    statusUpdatedField: 'OutstandingClaimStatusUpdatedAt',
    amountField: 'ClaimFee',
    goalSetting: null,
    responsibleSetting: 'OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID',
    label: 'Claims',
  },
  {
    key: 'patientBalances',
    index: 6,
    subject: 'patient_balances',
    statusUpdatedField: 'PatientBalancesStatusUpdatedAt',
    amountField: 'PatientAmount',
    goalSetting: null,
    responsibleSetting: 'PATIENT_BALANCES_RESPONSIBLE_EMPLOYEEID',
    label: 'Balances',
  },
  {
    key: 'outstandingPreAuths',
    index: 5,
    subject: 'outstanding_preauths',
    statusUpdatedField: 'OutstandingPreAuthStatusUpdatedAt',
    amountField: null,
    goalSetting: null,
    responsibleSetting: 'OUTSTANDING_PREAUTHS_RESPONSIBLE_EMPLOYEEID',
    label: 'Pre-auths',
  },
];

const QUEUE_BY_KEY = {};
const QUEUE_ORDER = {};
DASHBOARD_QUEUES.forEach((q, i) => {
  QUEUE_BY_KEY[q.key] = q;
  QUEUE_ORDER[q.key] = i;
});

export function dashboardQueueByKey(key) {
  return Object.prototype.hasOwnProperty.call(QUEUE_BY_KEY, key) ? QUEUE_BY_KEY[key] : null;
}

// ---- Days (moment's rules, without moment) ---------------------------------------------------

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
// 'YYYY-MM-DD', 'YYYY-MM-DD HH:mm[:ss[.SSS]]', 'YYYY-MM-DDTHH:mm…' — no offset: moment reads it as
// wall time IN its default zone, so its day is simply the date written.
const NAIVE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/;
// The same with 'Z' or '±HH:mm' / '±HHmm': a real instant.
const OFFSET_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s?(?:Z|[+-]\d{2}:?\d{2})$/i;

const FORMATTERS = {};
function pad2(n) { return n < 10 ? `0${n}` : String(n); }

/** 'YYYY-MM-DD' of an instant (ms) in `timeZone`; the machine's zone if the zone is unknown. */
export function zonedYmd(ms, timeZone) {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return null;
  const tz = timeZone || DEFAULT_TIME_ZONE;
  try {
    if (!FORMATTERS[tz]) {
      FORMATTERS[tz] = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
    }
    const parts = {};
    FORMATTERS[tz].formatToParts(d).forEach((p) => { parts[p.type] = p.value; });
    return `${parts.year}-${parts.month}-${parts.day}`;
  } catch (e) {
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }
}

/** An OFFSET_RE string → ms ('YYYY-MM-DD HH:mm+0500' normalised to ISO first). */
function parseOffset(value) {
  const iso = value.replace(' ', 'T').replace(/\s(?=Z|[+-]\d)/i, '').replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  return Date.parse(iso);
}

function validYmd(y, m, d) {
  const mm = Number(m);
  const dd = Number(d);
  return mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31 ? `${y}-${m}-${d}` : null;
}

/**
 * The "today" every rule compares against: opts.today ('YYYY-MM-DD') or opts.now (Date / ms) in
 * opts.timeZone. Never the clock (rule files never read it — followups-state.test.js): with
 * neither, null — no DATE ever matches and only the dueToday / pastDue flags count.
 */
function todayOf(opts) {
  const o = opts || {};
  if (typeof o.today === 'string' && YMD_RE.test(o.today)) return o.today;
  if (o.now instanceof Date) return zonedYmd(o.now.getTime(), o.timeZone);
  if (typeof o.now === 'number' && Number.isFinite(o.now)) return zonedYmd(o.now, o.timeZone);
  return null;
}

/**
 * The day moment(value) falls on, as 'YYYY-MM-DD', or null where moment would be invalid.
 * moment(undefined) is now → today.
 */
function dayOf(value, today, timeZone) {
  if (value === undefined) return today;
  if (value === null || value === '') return null;
  if (value instanceof Date) return zonedYmd(value.getTime(), timeZone);
  if (typeof value === 'number') return Number.isFinite(value) ? zonedYmd(value, timeZone) : null;
  if (typeof value !== 'string') return null;
  if (OFFSET_RE.test(value)) return zonedYmd(parseOffset(value), timeZone);
  const m = NAIVE_RE.exec(value);
  if (m) return validYmd(m[1], m[2], m[3]);
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : zonedYmd(ms, timeZone);
}

// ---- Rows ------------------------------------------------------------------------------------

function queueOf(queue) {
  if (typeof queue === 'string') return dashboardQueueByKey(queue);
  return queue && typeof queue === 'object' ? queue : null;
}

/**
 * The old `*DataToday` filter: created today, or worked today (the queue's *StatusUpdatedAt), or
 * due today, or past due. `queue` = a DASHBOARD_QUEUES entry, its key, or any { statusUpdatedField }.
 * @param {Object} opts  { today: 'YYYY-MM-DD' (REQUIRED in practice — the query's `d`), or now + timeZone }
 */
export function isTodayRow(row, queue, opts) {
  if (!row || typeof row !== 'object') return false;
  const q = queueOf(queue);
  // An unknown queue is never "today" (a typo must not put every row on the page).
  if (!q || !q.statusUpdatedField) return false;
  const o = opts || {};
  const today = todayOf(o);
  const tz = o.timeZone || DEFAULT_TIME_ZONE;
  return (today !== null && dayOf(row.CreatedAt, today, tz) === today)
    || (today !== null && dayOf(row[q.statusUpdatedField], today, tz) === today)
    || row.dueToday === 'true'
    || row.pastDue === 'true';
}

/** The old widgets' conversion: a boolean is kept; any other value is completed unless it is 'false'. */
export function isCompletedRow(row) {
  const v = row ? row.completed : undefined;
  if (typeof v === 'boolean') return v;
  return v !== 'false';
}

/**
 * adminData → the worklist's three buckets. Each item is { queueKey, row } (the row object itself,
 * never a copy — the drawer saves into it).
 *   done     passes isTodayRow and completed
 *   open     passes isTodayRow, not completed, and dueToday or pastDue === 'true'
 *   dropped  passes isTodayRow but neither — e.g. created / touched today with a future follow-up
 *            date, or a task whose StatusID is not 1/3 (the query then sets every flag 'false').
 *            The old widgets listed these as unchecked rows; the new page does not show them.
 * A queue is skipped when !can(subject) (the caller's $can('view', X) || $can('view', 'all')) —
 * a missing `can` hides everything — or when `queueKeys` (My queues) is given and lacks it.
 * Missing / non-array entries, null rows and a null adminData are tolerated (empty buckets).
 * Rows keep their adminData order within a queue; queues come in DASHBOARD_QUEUES order.
 */
export function splitDashboardRows(adminData, { can, queueKeys, today, timeZone, now } = {}) {
  const out = { open: [], done: [], dropped: [] };
  if (!Array.isArray(adminData)) return out;
  const allow = typeof can === 'function' ? can : () => false;
  const only = Array.isArray(queueKeys) ? queueKeys : null;
  const opts = { today: todayOf({ today, timeZone, now }), timeZone, now };
  DASHBOARD_QUEUES.forEach((q) => {
    if (only && only.indexOf(q.key) === -1) return;
    if (allow(q.subject) !== true) return;
    const rows = adminData[q.index];
    if (!Array.isArray(rows)) return;
    rows.forEach((row) => {
      if (!isTodayRow(row, q, opts)) return;
      const item = { queueKey: q.key, row };
      if (isCompletedRow(row)) out.done.push(item);
      else if (row.dueToday === 'true' || row.pastDue === 'true') out.open.push(item);
      else out.dropped.push(item);
    });
  });
  return out;
}

/**
 * "My queues": the queue keys whose responsible-employee list holds `userID` (string compare, ids
 * trimmed). `responsibleIdsByQueue` = { [queueKey]: ['id', …] } — build it with queues.js
 * responsibleEmployeeIds(queueByKey(key), allSettings). Returns [] for no user (the page then shows
 * an empty My queues, never Everyone's).
 */
export function myQueueKeys(responsibleIdsByQueue, userID) {
  if (userID === null || userID === undefined || String(userID).trim() === '') return [];
  const me = String(userID).trim();
  const map = responsibleIdsByQueue || {};
  return DASHBOARD_QUEUES
    .filter((q) => {
      const ids = map[q.key];
      return Array.isArray(ids) && ids.some(id => id !== null && id !== undefined && String(id).trim() === me);
    })
    .map(q => q.key);
}

/** Open items → the two sections. dueToday wins if a row (wrongly) says both. */
export function groupOpen(openItems) {
  const out = { dueToday: [], pastDue: [] };
  (Array.isArray(openItems) ? openItems : []).forEach((item) => {
    const r = item && item.row;
    if (!r) return;
    if (r.dueToday === 'true') out.dueToday.push(item);
    else if (r.pastDue === 'true') out.pastDue.push(item);
  });
  return out;
}

// ---- Sort ------------------------------------------------------------------------------------

export const SORT_KEYS = ['recent', 'oldest', 'amount'];

function toNumber(value) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** The item's amount (its queue's amountField), or null. */
export function amountOf(item) {
  const q = item ? dashboardQueueByKey(item.queueKey) : null;
  if (!q || !q.amountField || !item.row) return null;
  return toNumber(item.row[q.amountField]);
}

function ymdKey(value) {
  if (typeof value !== 'string') return null;
  const m = NAIVE_RE.exec(value) || OFFSET_RE.exec(value);
  return m ? validYmd(m[1], m[2], m[3]) : null;
}

function nameKey(row) {
  return row && row.Patient !== null && row.Patient !== undefined ? String(row.Patient).toLowerCase() : '';
}

/**
 * A sorted COPY. 'recent' = FollowupDate newest first, 'oldest' = oldest first (undated last in
 * both), 'amount' = largest amount first (no amount last). Ties: DASHBOARD_QUEUES order, then
 * patient name, then the input order. An unknown sortKey sorts as 'recent'.
 */
export function sortOpen(items, sortKey) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  const key = SORT_KEYS.indexOf(sortKey) === -1 ? 'recent' : sortKey;
  const decorated = list.map((item, i) => ({
    item,
    i,
    date: ymdKey(item.row ? item.row.FollowupDate : null),
    amount: amountOf(item),
    order: Object.prototype.hasOwnProperty.call(QUEUE_ORDER, item.queueKey) ? QUEUE_ORDER[item.queueKey] : DASHBOARD_QUEUES.length,
    name: nameKey(item.row),
  }));
  decorated.sort((a, b) => {
    let c = 0;
    if (key === 'amount') {
      if (a.amount === null && b.amount !== null) c = 1;
      else if (a.amount !== null && b.amount === null) c = -1;
      else if (a.amount !== null && b.amount !== null) c = b.amount - a.amount;
    } else {
      if (a.date === null && b.date !== null) c = 1;
      else if (a.date !== null && b.date === null) c = -1;
      else if (a.date !== null && b.date !== null && a.date !== b.date) {
        c = (a.date < b.date ? -1 : 1) * (key === 'oldest' ? 1 : -1);
      }
    }
    if (c !== 0) return c;
    if (a.order !== b.order) return a.order - b.order;
    if (a.name !== b.name) return a.name < b.name ? -1 : 1;
    return a.i - b.i;
  });
  return decorated.map(d => d.item);
}

// ---- Search ----------------------------------------------------------------------------------

function tokensOf(q) {
  return q === null || q === undefined ? [] : String(q).trim().toLowerCase().split(/\s+/).filter(Boolean);
}

function matchesAll(haystack, tokens) {
  const h = haystack.toLowerCase();
  return tokens.every(t => h.indexOf(t) !== -1);
}

function textOf(v) {
  return v === null || v === undefined ? '' : String(v);
}

/**
 * Search-as-you-type over the row's Patient and Carrier. Case-insensitive, trimmed; every word of
 * the query must appear somewhere ("smith delta" finds Smith, John at Delta Dental). Empty ⇒ true.
 */
export function matchesWorklistSearch(row, q) {
  const tokens = tokensOf(q);
  if (tokens.length === 0) return true;
  if (!row) return false;
  return matchesAll(`${textOf(row.Patient)} ${textOf(row.Carrier)}`, tokens);
}

/** The same rule for a task: its text (HTML already stripped by the caller) + its patients' names. */
export function matchesTaskSearch(text, patientNames, q) {
  const tokens = tokensOf(q);
  if (tokens.length === 0) return true;
  const names = Array.isArray(patientNames) ? patientNames.map(textOf) : [];
  return matchesAll([textOf(text)].concat(names).join(' '), tokens);
}

// ---- Chips -----------------------------------------------------------------------------------

/**
 * The chip row: All, Tasks, then one chip per visible queue (DASHBOARD_QUEUES order, count 0 kept).
 * `visibleQueueKeys` = the queues the user may see (absent ⇒ all six); open items of any other
 * queue are not counted. All = the queue chips + Tasks, so the numbers always add up.
 */
export function chipCounts(openItems, openTaskCount, visibleQueueKeys) {
  const visible = Array.isArray(visibleQueueKeys) ? visibleQueueKeys : DASHBOARD_QUEUES.map(q => q.key);
  const counts = {};
  (Array.isArray(openItems) ? openItems : []).forEach((item) => {
    if (item && item.queueKey) counts[item.queueKey] = (counts[item.queueKey] || 0) + 1;
  });
  const n = Number(openTaskCount);
  const tasks = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  const queueChips = DASHBOARD_QUEUES
    .filter(q => visible.indexOf(q.key) !== -1)
    .map(q => ({ key: q.key, label: q.label, count: counts[q.key] || 0 }));
  const total = queueChips.reduce((a, c) => a + c.count, 0) + tasks;
  return [{ key: 'all', label: 'All', count: total }, { key: 'tasks', label: 'Tasks', count: tasks }].concat(queueChips);
}

// ---- Done today ------------------------------------------------------------------------------

/** A sortable number for a raw time value: wall-clock strings compare as written. */
function timeKey(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value === '') return null;
  if (OFFSET_RE.test(value)) {
    const ms = parseOffset(value);
    return Number.isNaN(ms) ? null : ms;
  }
  const m = NAIVE_RE.exec(value);
  if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0));
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

function doneAtOf(item) {
  const q = dashboardQueueByKey(item.queueKey);
  const r = item.row || {};
  const worked = q && q.statusUpdatedField ? r[q.statusUpdatedField] : undefined;
  return worked !== null && worked !== undefined && worked !== '' ? worked : r.CreatedAt;
}

function empOf(row) {
  const v = row ? row.AssignedToEmpID : null;
  return v === null || v === undefined || String(v).trim() === '' ? null : String(v).trim();
}

/**
 * The "Done today" rail. items = newest first by the queue's *StatusUpdatedAt (CreatedAt when that
 * is empty); undated last; each { queueKey, patient, status, at (raw), empID, row }.
 * teammates = the distinct AssignedToEmpIDs that are not `userID` (AssignedToEmpID is a proxy for
 * "who did it" — the query has no worked-by column). earliestAt = the oldest `at` ("since H:MM").
 */
export function doneRail(doneItems, { userID } = {}) {
  const me = userID === null || userID === undefined ? null : String(userID).trim();
  const list = (Array.isArray(doneItems) ? doneItems : []).filter(it => it && it.row);
  const decorated = list.map((item, i) => {
    const at = doneAtOf(item);
    return { item, i, at, t: timeKey(at) };
  });
  decorated.sort((a, b) => {
    if (a.t === null && b.t !== null) return 1;
    if (a.t !== null && b.t === null) return -1;
    if (a.t !== null && b.t !== null && a.t !== b.t) return b.t - a.t;
    return a.i - b.i;
  });
  const items = decorated.map(d => ({
    queueKey: d.item.queueKey,
    patient: d.item.row.Patient === null || d.item.row.Patient === undefined ? '' : String(d.item.row.Patient),
    status: d.item.row.Status === null || d.item.row.Status === undefined ? '' : String(d.item.row.Status),
    at: d.at === undefined ? null : d.at,
    empID: empOf(d.item.row),
    row: d.item.row,
  }));
  const teammates = [];
  let includesMe = false;
  items.forEach((it) => {
    if (it.empID === null) return;
    if (me !== null && me !== '' && it.empID === me) includesMe = true;
    else if (teammates.indexOf(it.empID) === -1) teammates.push(it.empID);
  });
  const dated = decorated.filter(d => d.t !== null);
  const earliest = dated.length ? dated[dated.length - 1] : null;
  return {
    items,
    count: items.length,
    teammates,
    teammateCount: teammates.length,
    includesMe,
    earliestAt: earliest ? earliest.at : null,
  };
}

// ---- Call goals ------------------------------------------------------------------------------

function goalNumber(v) {
  const s = v === null || v === undefined ? '' : String(v).trim();
  if (s === '') return 0;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * The combined daily call goals ("4 / 13"). goalValues = { overdueRecall, unscheduledTreatment }
 * (absent / NaN / ≤ 0 ⇒ 0 — that queue has no goal). done counts completed items ONLY of queues
 * with a goal, as the old two progress bars did. Optional `visibleQueueKeys` drops the goal of a
 * queue the user cannot see (the old bar sat inside the widget's permission check).
 * Pass the EVERYONE done list: the goal is the office's, not one person's (old page: every row).
 * pct is a whole number 0–100, floored, so 100 means the goal is really hit.
 */
export function callGoals(doneItems, goalValues, visibleQueueKeys) {
  const g = goalValues || {};
  const visible = Array.isArray(visibleQueueKeys) ? visibleQueueKeys : null;
  const goals = {};
  DASHBOARD_QUEUES.forEach((q) => {
    if (!q.goalSetting) return;
    if (visible && visible.indexOf(q.key) === -1) return;
    const n = goalNumber(g[q.key]);
    if (n > 0) goals[q.key] = n;
  });
  const goal = Object.keys(goals).reduce((a, k) => a + goals[k], 0);
  const done = (Array.isArray(doneItems) ? doneItems : [])
    .filter(it => it && it.row && Object.prototype.hasOwnProperty.call(goals, it.queueKey))
    .length;
  const pct = goal > 0 ? Math.min(100, Math.max(0, Math.floor((done / goal) * 100))) : 0;
  return { show: goal > 0, done, goal, pct, left: Math.max(0, goal - done) };
}
